import * as React from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { get } from "lodash";

import type { OptionType } from "components/select-form-field/select-form-field.component";

type SearchableSelectFormFieldProps = {
  options: OptionType[];
  name: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  sx?: SxProps;
};

export type RhfSearchableSelectFormFieldProps<T extends FieldValues> =
  SearchableSelectFormFieldProps & UseControllerProps<T>;

export default function SearchableSelectFormField<T extends FieldValues>({
  name,
  label = "",
  disabled = false,
  control,
  options,
  required = false,
  sx
}: RhfSearchableSelectFormFieldProps<T>) {
  const {
    field: { onChange, onBlur, value, ref },
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: {
      required: required
        ? { value: true, message: `${label || "Selection"} is required` }
        : undefined
    }
  });

  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  const selectedOption =
    value !== null && value !== undefined && value !== ""
      ? (options.find((o) => o.value === value) ?? null)
      : null;

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      disabled={disabled}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      popupIcon={<ExpandMoreOutlinedIcon />}
      onBlur={onBlur}
      onChange={(_, option) => {
        onChange(option?.value ?? null);
        onBlur();
      }}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={ref}
          label={label}
          required={required}
          error={Boolean(errorMessage)}
          helperText={errorMessage || ""}
        />
      )}
    />
  );
}